/**
 * Predecessor picker state and commitPredecessorLinkChanges for /schedule/tasks.
 * Route: /schedule/tasks.
 * Does not own the DHTMLX chart engine or save/discard session sync.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ITaskStore } from '../../../stores/ITaskStore';
import { findGanttLinkByEndpoints, repopulateGanttLinkPointers, resolvePredecessorAffectedLinkIds, buildPredecessorDisplayText, applyPredecessorInputToStore } from '../../../gantt/taskLinkDisplayUtils';
import { notifications } from '@mantine/notifications';
import { validatePredecessorLink } from '../../../gantt/predecessorValidation';
import type { PredecessorLinkChange } from '../../../gantt/taskLinkDisplayUtils';
import { loadPredecessorGridShell } from '../components/predecessor-picker';
import type { PredecessorGridShell } from '../components/predecessor-picker';

export function useGanttPredecessors(p: Record<string, any>) {
  const {
    ganttRef,
    predecessorLinkCommitInProgressRef,
    autoScheduleEnabled,
    scheduleBackgroundPersist,
    dhtmlxTasks,
    resolvePredecessorWbsCodeRef,
  } = p;
  const store = p.store as ITaskStore;

const [predecessorPickerOpen, setPredecessorPickerOpen] = useState(false);
const [predecessorPickerTaskId, setPredecessorPickerTaskId] = useState<string | null>(null);
const predecessorPickerTaskIdRef = useRef<string | null>(predecessorPickerTaskId);
predecessorPickerTaskIdRef.current = predecessorPickerTaskId;
const [predecessorPickerMode, setPredecessorPickerMode] = useState<'grid' | 'panel'>('grid');
const [predecessorPickerInputTick, setPredecessorPickerInputTick] = useState(0);
/** Grid-only shell: drawer vs floating (session-persisted). */
const [predecessorGridShell, setPredecessorGridShell] = useState<PredecessorGridShell>(() =>
  loadPredecessorGridShell(),
);
const predecessorPanelDraftRef = useRef<{
  getDraft: () => string;
  applyDraft: (value: string) => void;
} | null>(null);
const openPredecessorPickerRef = useRef<(taskId: string, mode?: 'grid' | 'panel') => void>(
  () => {},
);

useEffect(() => {
  openPredecessorPickerRef.current = (taskId: string, mode: 'grid' | 'panel' = 'grid') => {
    setPredecessorPickerMode(mode);
    setPredecessorPickerTaskId(taskId);
    setPredecessorPickerOpen(true);
    setPredecessorPickerInputTick((n) => n + 1);
  };
}, []);

/**
 * Shared path for inline grid + task panel predecessor edits.
 * Keeps session affected tasks/links, gantt link graph, and auto-schedule in sync.
 */
const commitPredecessorLinkChanges = useCallback(
  (
    taskId: string,
    change: PredecessorLinkChange,
    options?: { ganttLinksAlreadySynced?: boolean },
  ) => {
    const ganttLinksAlreadySynced = options?.ganttLinksAlreadySynced ?? false;
    const gantt = ganttRef.current?.instance as {
      isLinkExists?: (id: number | string) => boolean;
      deleteLink?: (id: number | string) => void;
      addLink?: (link: {
        id: number | string;
        source: string;
        target: string;
        type: string;
        lag: number | null;
      }) => number | string;
      getLinks: () => Array<{
        id: number | string;
        source: string | number;
        target: string | number;
        type?: string | number;
        lag?: number | null;
      }>;
      eachTask: (cb: (task: { $source?: unknown[]; $target?: unknown[] }) => void) => void;
      isTaskExists: (id: string | number) => boolean;
      getTask: (id: string | number) => { $source?: unknown[]; $target?: unknown[] };
      refreshTask?: (id: string | number) => void;
      render?: () => void;
      config?: { auto_scheduling?: boolean };
      autoSchedule?: (id: string) => void;
    } | undefined;

    predecessorLinkCommitInProgressRef.current = true;
    try {
      const tasksSnapshot = store.getTaskValues();
      const pendingAdded =
        change.addedLinks?.length > 0
          ? change.addedLinks
          : change.addedLinkIds
              .map((linkId) => store.getLinks().find((l) => l.id === linkId))
              .filter((l): l is NonNullable<typeof l> => Boolean(l));
      for (const snap of pendingAdded) {
        const check = validatePredecessorLink(
          String(snap.source),
          String(snap.target),
          tasksSnapshot,
          store.getLinks(),
        );
        if (!check.ok) {
          notifications.show({
            title: 'Invalid predecessor',
            message: check.reason,
            color: 'red',
          });
          return;
        }
      }

      if (gantt && !ganttLinksAlreadySynced) {
        // Batch all link deletes/adds first. Running auto-schedule after each delete
        // (replace path) merges gantt↔store while the new link is not in gantt yet and
        // can race ReactGantt link sync, leaving the Predecessor cell empty.
        for (const link of change.removedLinks) {
          const existsById =
            gantt.isLinkExists?.(link.id) || gantt.isLinkExists?.(String(link.id));
          if (existsById) {
            gantt.deleteLink?.(link.id);
          } else {
            const match = findGanttLinkByEndpoints(gantt, link.source, link.target);
            if (match && (gantt.isLinkExists?.(match.id) || gantt.isLinkExists?.(String(match.id)))) {
              gantt.deleteLink?.(match.id);
            }
          }
        }

        const addedSnaps = pendingAdded;

        for (const snap of addedSnaps) {
          const alreadyThere = findGanttLinkByEndpoints(gantt, snap.source, snap.target);
          if (!alreadyThere) {
            if (!gantt.isLinkExists?.(snap.id) && !gantt.isLinkExists?.(String(snap.id))) {
              gantt.addLink?.({
                id: snap.id,
                source: String(snap.source),
                target: String(snap.target),
                type: String(snap.type ?? '0'),
                lag: snap.lag ?? 0,
              });
            }
          }
          const inGantt = findGanttLinkByEndpoints(gantt, snap.source, snap.target);
          if (inGantt && inGantt.id !== snap.id) {
            store.removeLink(snap.id);
            store.addLink(inGantt);
          }
        }

        repopulateGanttLinkPointers(gantt);
        if (change.affectedTaskIds.length > 0) {
          store.addAffectedTasks(change.affectedTaskIds);
        } else {
          for (const link of change.removedLinks) {
            store.addAffectedTasks([link.source, link.target]);
          }
          for (const snap of addedSnaps) {
            store.addAffectedTasks([String(snap.source), String(snap.target)]);
          }
        }
        store.addEditedTasks([String(taskId)], ['predecessors']);

        if (autoScheduleEnabled && gantt.autoSchedule) {
          gantt.autoSchedule(String(taskId));
        }
      } else if (gantt) {
        repopulateGanttLinkPointers(gantt);
        if (change.affectedTaskIds.length > 0) {
          store.addAffectedTasks(change.affectedTaskIds);
        }
        store.addEditedTasks([String(taskId)], ['predecessors']);
        if (autoScheduleEnabled && gantt.autoSchedule) {
          gantt.autoSchedule(String(taskId));
        }
      }

      const { removed, added } = resolvePredecessorAffectedLinkIds(change, {
        ganttLinksAlreadySynced,
        findLinkByEndpoints: (source, target) =>
          gantt ? findGanttLinkByEndpoints(gantt, source, target) : null,
        getStoreLink: (id) => store.getLinks().find((link) => link.id === id),
      });
      store.trackAffectedLinkChanges(removed, added);

      // Predecessor grid cells read gantt links / $target; force a redraw after sync.
      if (gantt) {
        repopulateGanttLinkPointers(gantt);
        for (const id of change.affectedTaskIds) {
          if (gantt.isTaskExists?.(id)) {
            gantt.refreshTask?.(id);
          }
        }
        gantt.render?.();
      }

      scheduleBackgroundPersist('predecessor-links');
    } finally {
      predecessorLinkCommitInProgressRef.current = false;
    }
  },
  [store, autoScheduleEnabled, scheduleBackgroundPersist],
);

const closePredecessorPicker = useCallback(() => {
  setPredecessorPickerOpen(false);
  setPredecessorPickerTaskId(null);
  setPredecessorPickerMode('grid');
}, []);

const handleTaskPanelPredecessorUpdate = useCallback(
  (taskId: string, change: PredecessorLinkChange) => {
    commitPredecessorLinkChanges(taskId, change);
  },
  [commitPredecessorLinkChanges],
);

const predecessorPickerCurrentInput = useMemo(() => {
  if (!predecessorPickerTaskId) return '';
  if (predecessorPickerMode === 'panel' && predecessorPanelDraftRef.current) {
    return predecessorPanelDraftRef.current.getDraft();
  }
  return buildPredecessorDisplayText(
    predecessorPickerTaskId,
    store.getLinks(),
    (id) => store.getTask(id) ?? null,
  );
}, [predecessorPickerTaskId, predecessorPickerMode, store, dhtmlxTasks, predecessorPickerInputTick]);

const handlePredecessorPickerApplyInput = useCallback(
  (input: string) => {
    const taskId = predecessorPickerTaskId;
    if (!taskId) return;

    if (predecessorPickerMode === 'panel' && predecessorPanelDraftRef.current) {
      predecessorPanelDraftRef.current.applyDraft(input);
      setPredecessorPickerInputTick((n) => n + 1);
      return;
    }

    try {
      const change = applyPredecessorInputToStore({
        taskId,
        input,
        tasks: store.getTaskValues(),
        links: store.getLinks(),
        resolveWbsCode: resolvePredecessorWbsCodeRef.current,
        addLink: (link) => store.addLink(link),
        removeLink: (id) => store.removeLink(id),
      });
      commitPredecessorLinkChanges(taskId, change);
      setPredecessorPickerInputTick((n) => n + 1);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'That predecessor is not allowed.';
      notifications.show({
        title: 'Invalid predecessor',
        message,
        color: 'red',
      });
    }
  },
  [
    predecessorPickerTaskId,
    predecessorPickerMode,
    store,
    resolvePredecessorWbsCodeRef,
    commitPredecessorLinkChanges,
  ],
);

  return {
    predecessorPickerOpen,
    setPredecessorPickerOpen,
    predecessorPickerTaskId,
    setPredecessorPickerTaskId,
    predecessorPickerMode,
    setPredecessorPickerMode,
    predecessorPickerInputTick,
    setPredecessorPickerInputTick,
    predecessorGridShell,
    setPredecessorGridShell,
    predecessorPickerTaskIdRef,
    predecessorPanelDraftRef,
    openPredecessorPickerRef,
    commitPredecessorLinkChanges,
    closePredecessorPicker,
    handleTaskPanelPredecessorUpdate,
    predecessorPickerCurrentInput,
    handlePredecessorPickerApplyInput,
  };
}

